/**
 * The signed money a settled bet moved on the balance — the ONE number a bet
 * row may print with a sign.
 *
 * House rule (D-43): a `+`/`−` always means net change to the balance, never a
 * gross return. `payout` is stake × odds and includes the stake, so a R$ 2 bet at
 * ×2 returns R$ 4 but only *earns* R$ 2. Printing `+R$ 4` next to a settlement
 * card that says `Resultado −R$ 2 · Retorno total R$ 0` read as two different
 * accountings of the same money. The bet-history sheet already sums net, so the
 * in-room bet rows must too.
 *
 * `null` means "nothing to sign yet" (pending) and lets the caller render a dash.
 */
export type SettleableBet = {
  result: 'pending' | 'win' | 'lose' | 'cancelled';
  amount: number;
  payout: number;
};

export function betNetAmount(bet: SettleableBet): number | null {
  const stake = Number(bet.amount || 0);
  if (bet.result === 'win') return Number(bet.payout || 0) - stake;
  if (bet.result === 'lose') return -stake;
  if (bet.result === 'cancelled') return 0;
  return null;
}

/** `+R$ 2,00` / `−R$ 2,00` / `R$ 0,00` — sign only when the balance actually moved. */
export function signedNet(net: number, fmt: (abs: number) => string): string {
  if (net > 0) return `+${fmt(net)}`;
  if (net < 0) return `−${fmt(-net)}`;
  return fmt(0);
}
